'use client';

import { useCallback, useRef, useSyncExternalStore } from 'react';

interface UseHideOnScrollOptions {
  /** Minimum scroll delta (px) before toggling — avoids jitter. Default 10. */
  threshold?: number;
  /**
   * Scroll distance (px) from the top that must be exceeded before the element
   * may hide. Keeps the element visible while its own height is still on
   * screen so hiding it never exposes a white gap (EWM-551). Set this to the
   * header's height. Default 0 (may hide as soon as scrolled off the top).
   */
  offset?: number;
  /** When false the element is never hidden (e.g. desktop). Default true. */
  enabled?: boolean;
}

// EWM-551 — drives a hide-on-scroll header. Returns whether a fixed element
// should be hidden based on scroll direction: it hides on scroll-down, reveals
// on scroll-up past `threshold`, and is always shown within `offset` px of the
// top of the page. When `enabled` is false it never hides (desktop keeps the
// header pinned). SSR-safe — the listener is attached via useSyncExternalStore's
// subscribe, so the server snapshot (`false`) renders first and the real value
// takes over once a scroll event fires on the client.
const useHideOnScroll = ({ threshold = 10, offset = 0, enabled = true }: UseHideOnScrollOptions = {}): boolean => {
  // EWM-760 — `hidden` is derived from scroll history (direction, threshold,
  // offset), not just the current scrollY, so the bookkeeping (lastY) can't
  // live in getSnapshot itself. It's kept as plain closure state inside
  // subscribe (recreated whenever enabled/threshold/offset change, same as
  // the old effect's dependency array) and mutates a ref that getSnapshot
  // reads — the ref is the "external store", subscribe is where the listener
  // + history live, and there is no setState in an effect.
  const hiddenRef = useRef(false);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!enabled) {
        hiddenRef.current = false;
        onStoreChange();
        return () => {};
      }

      let lastY = window.scrollY;

      const onScroll = () => {
        const y = window.scrollY;
        // Always show while within the header's own height of the top: hiding it
        // here would slide the fixed header away while its spacer is still on
        // screen, exposing a white gap (EWM-551). Only once scrolled past the
        // header height has the spacer left the viewport, so hiding is seamless.
        if (y <= offset) {
          hiddenRef.current = false;
          lastY = y;
          onStoreChange();
          return;
        }
        const delta = y - lastY;
        // Ignore sub-threshold movement so the header doesn't jitter.
        if (Math.abs(delta) < threshold) return;
        hiddenRef.current = delta > 0; // scrolling down → hide; up → show
        lastY = y;
        onStoreChange();
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    },
    [enabled, threshold, offset],
  );

  const getSnapshot = useCallback(() => hiddenRef.current, []);
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

export default useHideOnScroll;
